/**
 * Decomposition Utilities - Single Source of Truth
 * Pure functions for decomposing style values.
 * Used by StyleMetadataEngine, CSSPropertiesEngine, and CSSCustomPropertiesEngine.
 * @module abstractStyles/decompositionUtils
 */

import {
  decomposePropertiesWithNesting,
  decomposePropertiesWithDefinitionsAndNesting,
  type AtomicValueWithNesting,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { AggregatedStyleValues } from './types';

/**
 * Decomposes CSS properties to atomic values with nesting level tracking.
 * This is the single source of truth for CSS property decomposition.
 *
 * @param cssProps - Raw CSS properties (e.g., { border: '1px solid red', color: 'blue' })
 * @returns Decomposed atomics with nesting levels
 *
 * @example
 * const decomposed = decomposeCSSProperties({ border: '1px solid red' });
 * // Returns: { borderTopWidth: [{ value: '1px', nestingLevel: 2 }], ... }
 */
export function decomposeCSSProperties(
  cssProps: Record<string, string>,
): Record<string, AtomicValueWithNesting[]> {
  if (Object.keys(cssProps).length === 0) {
    return {};
  }

  return decomposePropertiesWithNesting(cssProps);
}

/**
 * Decomposes CSS custom properties, handling shorthand types.
 * Custom properties can have shorthand types (e.g., --border-theme: { type: 'border' })
 * which need to be decomposed using the same logic as CSS properties.
 *
 * @param customProps - Raw custom properties (e.g., { '--border-theme': '2px solid red' })
 * @param definitions - Custom property definitions with types
 * @returns Decomposed atomics with nesting levels
 *
 * @example
 * const decomposed = decomposeCustomProperties(
 *   { '--border-theme': '2px solid red' },
 *   { '--border-theme': { type: 'border' } }
 * );
 * // Returns: { borderTopWidth: [{ value: '2px', nestingLevel: 2 }], ... }
 */
export function decomposeCustomProperties(
  customProps: Record<string, string>,
  definitions: Record<string, any>,
): Record<string, AtomicValueWithNesting[]> {
  if (Object.keys(customProps).length === 0) {
    return {};
  }

  const decomposableValues: Record<string, string> = {};

  // For each custom property, check if it has a shorthand type
  for (const [key, value] of Object.entries(customProps)) {
    const definition = definitions[key];
    const cssPropertyType =
      (definition as any)?.type || (definition as any)?.cssPropertyType;

    if (cssPropertyType && cssPropertyType !== key) {
      // Has a shorthand type (e.g., type: 'border')
      // Use the type for decomposition
      decomposableValues[cssPropertyType] = value;
    } else {
      // Atomic custom property - keep as-is for decomposition
      decomposableValues[key] = value;
    }
  }

  return decomposePropertiesWithNesting(decomposableValues);
}

/**
 * Merges decomposed CSS properties and custom properties.
 * Used when both namespaces are present.
 *
 * @param cssPropsDecomposed - Decomposed CSS properties
 * @param customPropsDecomposed - Decomposed custom properties
 * @returns Merged decomposed values
 */
export function mergeDecomposedValues(
  cssPropsDecomposed: Record<string, AtomicValueWithNesting[]>,
  customPropsDecomposed: Record<string, AtomicValueWithNesting[]>,
): {
  cssProperties: Record<string, AtomicValueWithNesting[]>;
  cssCustomProperties: Record<string, AtomicValueWithNesting[]>;
} {
  return {
    cssProperties: cssPropsDecomposed,
    cssCustomProperties: customPropsDecomposed,
  };
}

/**
 * Decomposes CSS properties with eligibility information.
 * Properties defined in metadata but without values will appear with empty arrays,
 * allowing controls to distinguish between "not supported" and "supported but no value".
 *
 * @param cssProps - Raw CSS properties (e.g., { border: '1px solid red', color: 'blue' })
 * @param definitions - Array of property keys defined in manifest
 * @returns Decomposed atomics with nesting levels, including empty arrays for eligible properties without values
 *
 * @example
 * const decomposed = decomposeCSSPropertiesWithEligibility(
 *   { color: 'red' },
 *   ['border', 'color'] // border is defined but has no value
 * );
 * // Returns: {
 * //   borderTopWidth: [], // eligible but no value
 * //   borderRightWidth: [], // eligible but no value
 * //   ...
 * //   color: [{ value: 'red', nestingLevel: 0 }]
 * // }
 */
export function decomposeCSSPropertiesWithEligibility(
  cssProps: Record<string, string>,
  definitions: string[],
): Record<string, AtomicValueWithNesting[]> {
  // Use enhanced function that returns nesting levels directly
  return decomposePropertiesWithDefinitionsAndNesting(definitions, cssProps);
}

export const getCurrentDecomposedValues = (
  values: AggregatedStyleValues | null,
): Record<string, AtomicValueWithNesting[]> => {
  if (!values) {
    return {};
  }

  return values.mode === 'cssProperties'
    ? values.decomposed.cssProperties
    : values.mode === 'cssCustomProperties'
    ? values.decomposed.cssCustomProperties
    : {
        ...values.decomposed.cssProperties,
        ...values.decomposed.cssCustomProperties,
      };
};
